import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { executePayouts } from 'lib/api/payments';
import { NestedPayment, Payout } from 'lib/api/payments/types';
import NomineeExporter from 'lib/common/NomineeExporter';
import { Payment } from 'lib/common/NomineeExporter/types';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { formatDate, hasAnyPermissions, showAlert } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

type PayoutItem = Payout | NestedPayment;

type ExecuteGlobalPaymentsProps = {
  open: boolean;
  onClose: () => void;
  payouts: PayoutItem[];
  setUp: () => void;
};

const ExecuteGlobalPayments = (props: ExecuteGlobalPaymentsProps) => {
  const { open, onClose, payouts, setUp } = props;
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const newFinancingDate = new Date();
  newFinancingDate.setUTCHours(12, 0, 0, 0);
  const [selectedPayouts, setSelectedPayouts] = useState<number[]>(
    payouts.filter((payout) => !payout.executedAt).map((payout) => payout.id) ||
      [],
  );
  const [openFinancingDateDialog, setOpenFinancingDateDialog] = useState(false);
  const [openNomineeExporter, setOpenNomineeExporter] = useState(false);
  const [executedAt, setExecutedAt] = useState(newFinancingDate);

  const { mutate: executePayoutsMutation, isPending: isExecutingPayouts } =
    executePayouts(dispatch);

  const cantSelectRow = (row: PayoutItem) => {
    return row.executedAt;
  };

  const canSelectAll = () => {
    return payouts.every((payout) => !payout.executedAt);
  };

  const executePayments = () => {
    executePayoutsMutation(
      {
        payoutIds: selectedPayouts,
        executedAt: executedAt.toISOString(),
      },
      {
        onSuccess: (data) => {
          if (data && data.notProcessedPayoutIds.length > 0) {
            showAlert(
              dispatch,
              'warning',
              'Algunos pagos no pudieron ser completados: ' +
                data.notProcessedPayoutIds.join(', '),
            );
          } else {
            showAlert(dispatch, 'success', 'Pagos completados correctamente');
          }
          onClose();
          if (setUp) {
            void setUp();
          }
        },
        onError: () => {
          showAlert(dispatch, 'error', 'Error al completar los pagos');
        },
      },
    );
  };

  const handleFinancingAtChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = new Date(e.target.value);
    date.setUTCHours(12);
    setExecutedAt(date);
  };

  const buttons: CustomButtonProps[] = [
    {
      action: () => setOpenNomineeExporter(true),
      name: 'Exportar',
      color: 'secondary',
    },
    {
      action: () => setOpenFinancingDateDialog(true),
      name: 'Completar pagos',
      color: 'primary',
      disabled:
        selectedPayouts.length === 0 ||
        !hasAnyPermissions(user, ['financing_payout_execute']),
    },
  ];

  if (isExecutingPayouts) return <CenteredCircularProgress type="dialog" />;

  const paymentsForExporter: Payment[] = payouts
    .filter((payout) => selectedPayouts.includes(payout.id))
    .map((payout) => {
      return {
        id: payout.id,
        bankAccountOriginName: payout.bankAccountOriginName,
        bankAccountOriginNumber: payout.bankAccountOriginNumber,
        bankAccountOriginAccountType: payout.bankAccountOriginAccountType,
        bankAccountOriginBankCode: payout.bankAccountOriginBankCode,
        bankAccountOriginHolderIdentifier:
          payout.bankAccountOriginHolderIdentifier,
        bankAccountOriginHolderName: payout.bankAccountOriginHolderName,
        bankAccountOriginHolderEmail:
          payout.bankAccountOriginHolderEmail || undefined,
        bankAccountDestinationName: payout.bankAccountDestinationName,
        bankAccountDestinationNumber: payout.bankAccountDestinationNumber,
        bankAccountDestinationAccountType:
          payout.bankAccountDestinationAccountType,
        bankAccountDestinationBankCode: payout.bankAccountDestinationBankCode,
        bankAccountDestinationHolderIdentifier:
          payout.bankAccountDestinationHolderIdentifier,
        bankAccountDestinationHolderName:
          payout.bankAccountDestinationHolderName,
        amount: payout.amount,
        currency: payout.currency,
        executedAt: payout.executedAt,
        source: payout.source,
        sourceId: payout.sourceId,
        sourceTranslation: payout.sourceTranslation,
      };
    });

  return (
    <>
      <Dialog open={open} maxWidth="lg" fullWidth>
        <DialogTitle>Completar pagos</DialogTitle>
        <DialogContent>
          <HeaderWithActions
            title="Acá puedes ver los pagos a completar."
            variant="body1"
            buttons={buttons}
          />
          <EnhancedTable
            dataHeaders={[
              { key: 'businessOriginName', label: 'Cuenta Origen' },
              { key: 'bankAccountOriginName', label: 'Banco Origen' },
              { key: 'businessDestinationName', label: 'Cuenta Destino' },
              { key: 'bankAccountDestinationName', label: 'Banco Destino' },
              { key: 'amount', label: 'Monto', type: 'money' },
              { key: 'sourceTranslation', label: 'Fuente' },
              { key: 'sourceId', label: 'ID Fuente', type: 'numeric-id' },
            ]}
            data={payouts}
            selected={selectedPayouts}
            setSelected={setSelectedPayouts}
            canSelectAll={canSelectAll()}
            cantSelectRow={cantSelectRow}
            finalSumRow
          />
          <NomineeExporter
            open={openNomineeExporter}
            setOpen={setOpenNomineeExporter}
            payments={paymentsForExporter}
          />
        </DialogContent>
        <DialogActions>
          <CustomButton color="secondary" onClick={onClose}>
            Cancelar
          </CustomButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openFinancingDateDialog}
        onClose={() => setOpenFinancingDateDialog(false)}
      >
        <DialogTitle>Completar pagos</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro que deseas completar los {selectedPayouts.length}{' '}
            pagos seleccionados?
          </Typography>
          <Typography>
            Estos serán completados con la fecha de financiamiento:
          </Typography>
          <div style={{ marginTop: 10, textAlign: 'center' }}>
            <TextField
              value={formatDate(executedAt)}
              onChange={handleFinancingAtChange}
              type="date"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <CustomButton
            color="secondary"
            onClick={() => setOpenFinancingDateDialog(false)}
          >
            Cancelar
          </CustomButton>
          <CustomButton color="primary" onClick={executePayments}>
            Confirmar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExecuteGlobalPayments;
